import { useHeaderTitle } from '@blend-ed/blendx-platform';
import { EmptyState, Spinner } from '@blend-ed/blendx-ui';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import React, { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useFetchTicketById from '../../../hooks/support/useFetchTicketById';
import useInsertTicketComment from '../../../hooks/support/useInsertTicketComment';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import messages from './messages';
import TicketHeader from './TicketHeader';

function ViewTicket() {
    const { ticketId } = useParams();
    const { authenticatedUser } = useContext(AppContext);
    const { setTitle } = useHeaderTitle();
    const intl = useIntl();

    const { data: ticketDataFetch, loading, error, refetch } = useFetchTicketById(ticketId);
    const { insertTicketComment } = useInsertTicketComment();

    useEffect(() => {
        setTitle(intl.formatMessage(messages.supportTicket))
    }, [ticketDataFetch])

    const ticketData = useMemo(() => {
        if (ticketDataFetch && !loading) {
            return {
                id: ticketDataFetch.id,
                status: ticketDataFetch.status,
                priority: ticketDataFetch.priority,
                title: ticketDataFetch.title,
                description: ticketDataFetch.description,
                assignedTo: {
                    name: ticketDataFetch.assignedTo,
                    avatar: ticketDataFetch.assignedTo
                },
                createdAt: ticketDataFetch.createdAt,
                type: ticketDataFetch.type,
                updatedAt: ticketDataFetch.updatedAt,
                messages: ticketDataFetch.ticketUpdates.map((message) => ({
                    id: message.id,
                    sender: message.updatedBy === authenticatedUser.id ? 'learner' : 'support',
                    content: message.comment,
                    timestamp: message.createdAt,
                    userImage: authenticatedUser.id === message.updatedBy ? authenticatedUser.profileImage.imageUrlSmall : ""
                }))
            }
        }
    }, [ticketDataFetch]);

    const handleSendMessage = async (messageContent) => {
        insertTicketComment({
            variables: {
                ticketId: ticketId,
                updatedBy: authenticatedUser.id,
                comment: messageContent
            },
            onCompleted: () => {
                refetch();
            },
            onError: (error) => {
                console.error('Error sending message:', error);
            }
        });
    };

    if (loading) {
        return <Spinner />
    }

    if (error) {
        return <div>
            <FormattedMessage
                id="support.ticket.error"
                defaultMessage="Error: {error}"
                values={{ error: error.message }}
            />
        </div>
    }

    return (
        <div className="view-ticket">
            <div>
                <TicketHeader ticket={ticketData} />

                <div className="view-ticket__conversation">
                    {ticketData.messages.length === 0 && (
                        <div className="view-ticket__conversation-empty">
                            <EmptyState
                                type="box"
                                message={intl.formatMessage(messages.noMessagesYet)}
                            />
                        </div>
                    )}

                    {ticketData.messages.map((message) => (
                        <MessageBubble key={message.id} message={message} />
                    ))}
                </div>
            </div>

            <MessageInput onSendMessage={handleSendMessage} />
        </div>
    );
}

export default ViewTicket;